import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ThreeDot } from 'assets/icons/ThreeDot.svg';
import { useStyles } from '../styles';
import CertificateActionsMenu from './CertificateActionsMenu';
import moment from 'moment';
import { useAppSelector } from 'modules/state-mgmt/store';

import { Certificate } from '../types';

interface CertificateCardProps {
  certificate: Certificate;
  disabled?: boolean;
  onEdit: (certificateId: string) => void;
  onDelete: (certification: Certificate) => void;
}

const CertificateCard: React.FC<CertificateCardProps> = ({ certificate, disabled, onEdit, onDelete }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Get current project ID from the store
  const { currentProjectId } = useAppSelector(state => ({
    currentProjectId: state.onboarding?.temp?.project?.value,
  }));

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom - 24,
        right: window.innerWidth - rect.right + 18,
      });
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const getDisplayName = () => {
    // 1. Check if direct alias exists and return it
    if (certificate.alias) {
      return certificate.alias;
    }

    // 2. Check aliasesInProjects and find the current project's alias
    if (certificate.aliasesInProjects && certificate.aliasesInProjects.length > 0 && currentProjectId) {
      const projectAlias = certificate.aliasesInProjects.find(
        aliasInProject => aliasInProject.project.id === currentProjectId,
      );
      if (projectAlias && projectAlias.alias) {
        return projectAlias.alias;
      }
    }

    // 3. Default to certification name
    return certificate.certification.name;
  };

  const getInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '--';
    // Parse as UTC to avoid timezone issues
    return moment.utc(dateString).format('ddd MMM DD YYYY');
  };

  return (
    <div className={`${classes.certificateCard} ${classes.certificateCardSmall}`}>
      <div className={classes.certificateHeader}>
        <div className={classes.certificateHeaderContent}>
          <div className={classes.certificateAvatar}>{getInitial(getDisplayName())}</div>
          <div className={classes.certificateInfo}>
            <h3 className={classes.certificateTitle}>{getDisplayName()}</h3>
            <p className={classes.certificateDate}>{formatDate(certificate.createdAt)}</p>
          </div>
        </div>
        {!disabled && (
          <button ref={menuButtonRef} className={classes.menuButton} data-testid={`certificate-menu-button-${certificate.id}`} onClick={handleMenuToggle}>
            <ThreeDot />
          </button>
        )}
      </div>

      <div className={classes.certificateDetails}>
        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('certifications.certificationId')}</span>
          <span className={classes.detailValue}>{certificate.idNumber || '--'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('certifications.description')}</span>
          <span className={`${classes.detailValue} ${classes.detailValueClamped}`}>{certificate.description || '--'}</span>
        </div>

        <div className={`${classes.certificateDates} ${classes.certificateDatesMobile}`}>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('certifications.issueDate')}</span>
            <span className={classes.detailValue}>{formatDate(certificate.completionDate)}</span>
          </div>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('certifications.expirationDate')}</span>
            <span className={classes.detailValue}>
              {certificate.expirationDate ? formatDate(certificate.expirationDate) : '--'}
            </span>
          </div>
        </div>
      </div>

      <CertificateActionsMenu
        isOpen={isMenuOpen}
        onEdit={() => onEdit(certificate.id)}
        onDelete={() => onDelete(certificate)}
        onClose={() => setIsMenuOpen(false)}
        position={menuPosition}
      />
    </div>
  );
};

export default CertificateCard;
export type { Certificate };
